import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Resource {
  id: string
  name: string
  resource_type: string
  location: string
  capacity: number
  hourly_rate: number
  is_available: boolean
  amenities: string[]
}

interface Booking {
  id: string
  resource_name: string
  booked_by: string
  title: string
  start_time: string
  end_time: string
  status: string
  total_cost: number
  attendees: number
}

export default function ResourceBookingPage() {
  const [resources, setResources] = useState<Resource[]>([])
  const [bookings, setBookings] = useState<Booking[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [resourcesRes, bookingsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/resources/'),
      fetch(API_BASE + '/resources/bookings'),
      fetch(API_BASE + '/resources/statistics')
    ])
    if (resourcesRes.ok) setResources((await resourcesRes.json()).resources || [])
    if (bookingsRes.ok) setBookings((await bookingsRes.json()).bookings || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'completed': return 'bg-gray-100 text-gray-600'
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Resource Booking</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Booking</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Resources</div>
              <div className="text-2xl font-bold">{statistics.total_resources}</div>
              <div className="text-xs text-green-600">{statistics.available_now} available</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Today's Bookings</div>
              <div className="text-2xl font-bold">{statistics.bookings_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Utilization Rate</div>
              <div className="text-2xl font-bold">{statistics.utilization_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Revenue Today</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.revenue_today)}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Upcoming Bookings</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Booking</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resource</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {bookings.slice(0, 10).map(booking => (
                  <tr key={booking.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{booking.title}</div>
                      <div className="text-xs text-gray-500">{booking.booked_by}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{booking.resource_name}</div>
                      <div className="text-xs text-gray-500">{booking.attendees} attendees</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{formatTime(booking.start_time)}</div>
                      <div className="text-xs text-gray-500">to {formatTime(booking.end_time)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(booking.status)}`}>
                        {booking.status.replace(/_/g, ' ')}
                      </span>
                      <div className="text-xs text-green-600 mt-1">{formatCurrency(booking.total_cost)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Available Resources</h3>
            <div className="space-y-3">
              {resources.map(resource => (
                <div key={resource.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{resource.name}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${resource.is_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {resource.is_available ? 'Available' : 'Booked'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">{resource.resource_type} • {resource.location}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-500">Cap: {resource.capacity}</span>
                    <span className="text-sm font-medium text-blue-600">{formatCurrency(resource.hourly_rate)}/hr</span>
                  </div>
                  {resource.amenities.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {resource.amenities.slice(0, 3).map((amenity, idx) => (
                        <span key={idx} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">{amenity}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
